/**
 * Advanced Font Loader
 * Prevents FOUT (Flash of Unstyled Text) with progressive font loading
 * @version 1.0.0
 */

class AdvancedFontLoader {
  constructor() {
    this.fonts = new Map();
    this.loadedFonts = new Set();
    this.networkSpeed = this.detectNetworkSpeed();
    this.saveData = this.detectSaveData();
    this.config = {
      timeout: 3000, // Max time to wait for fonts
      fallbackDelay: 100, // Show fallback text after delay
      useSessionStorage: true
    };
    this.init();
  }

  init() {
    // Check if fonts were loaded in previous session
    if (this.config.useSessionStorage && this.areFontsCached()) {
      console.log('✅ Fonts already cached, using immediately');
      document.documentElement.classList.add('fonts-loaded');
      return;
    }

    // Detect available fonts
    this.detectFonts();

    // Load fonts progressively
    this.loadFonts();
  }

  /**
   * Detect network speed
   */
  detectNetworkSpeed() {
    if (!('connection' in navigator)) return 'fast';

    const conn = navigator.connection;
    const effectiveType = conn.effectiveType;

    const speedMap = {
      'slow-2g': 'slow',
      '2g': 'slow',
      '3g': 'medium',
      '4g': 'fast'
    };

    return speedMap[effectiveType] || 'fast';
  }

  /**
   * Detect Save-Data mode
   */
  detectSaveData() {
    if ('connection' in navigator && 'saveData' in navigator.connection) {
      return navigator.connection.saveData;
    }
    return false;
  }

  /**
   * Check if fonts are already cached
   */
  areFontsCached() {
    if (!this.config.useSessionStorage) return false;

    try {
      return sessionStorage.getItem('fonts-loaded') === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark fonts as cached
   */
  markFontsCached() {
    if (!this.config.useSessionStorage) return;

    try {
      sessionStorage.setItem('fonts-loaded', 'true');
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Detect fonts from stylesheets
   */
  detectFonts() {
    // Get all @font-face declarations
    const sheets = Array.from(document.styleSheets);

    sheets.forEach(sheet => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules || []);

        rules.forEach(rule => {
          if (rule instanceof CSSFontFaceRule) {
            const fontFamily = rule.style.fontFamily.replace(/['"]/g, '');
            const fontWeight = rule.style.fontWeight || '400';
            const fontStyle = rule.style.fontStyle || 'normal';

            const key = `${fontFamily}-${fontWeight}-${fontStyle}`;

            if (!this.fonts.has(key)) {
              this.fonts.set(key, {
                family: fontFamily,
                weight: fontWeight,
                style: fontStyle,
                descriptors: {
                  weight: fontWeight,
                  style: fontStyle
                }
              });
            }
          }
        });
      } catch (e) {
        // Cross-origin stylesheets may throw, skip them
      }
    });

    console.log(`🔤 Detected ${this.fonts.size} font variants`);
  }

  /**
   * Load fonts progressively
   */
  async loadFonts() {
    if (this.fonts.size === 0) {
      console.log('⚠️ No fonts detected to load');
      return;
    }

    // Save-Data: skip font loading, use system fonts
    if (this.saveData) {
      console.log('💾 Save-Data enabled, skipping custom fonts');
      document.documentElement.classList.add('fonts-fallback');
      return;
    }

    // Show fallback text briefly to prevent invisible text
    setTimeout(() => {
      if (!document.documentElement.classList.contains('fonts-loaded')) {
        document.documentElement.classList.add('fonts-loading');
      }
    }, this.config.fallbackDelay);

    // Group fonts by priority
    const criticalFonts = this.getCriticalFonts();
    const secondaryFonts = this.getSecondaryFonts();

    try {
      // Load critical fonts first (block rendering)
      if (criticalFonts.length > 0) {
        await Promise.race([
          this.loadFontGroup(criticalFonts, 'critical'),
          this.timeout(this.config.timeout)
        ]);
      }

      // Mark as loaded (even if some failed)
      document.documentElement.classList.remove('fonts-loading');
      document.documentElement.classList.add('fonts-loaded');
      this.markFontsCached();

      // Load secondary fonts in background
      if (secondaryFonts.length > 0) {
        this.loadFontGroup(secondaryFonts, 'secondary');
      }

    } catch (error) {
      console.warn('⚠️ Font loading timeout, showing fallback');
      document.documentElement.classList.remove('fonts-loading');
      document.documentElement.classList.add('fonts-fallback');
    }
  }

  /**
   * Get critical fonts (regular weight, normal style)
   */
  getCriticalFonts() {
    const critical = [];

    this.fonts.forEach((font, key) => {
      // Regular weight (400) and normal style are critical
      if (font.weight === '400' && font.style === 'normal') {
        critical.push(font);
      }
    });

    return critical;
  }

  /**
   * Get secondary fonts (bold, italic, etc.)
   */
  getSecondaryFonts() {
    const secondary = [];

    this.fonts.forEach((font, key) => {
      // All other weights and styles
      if (font.weight !== '400' || font.style !== 'normal') {
        secondary.push(font);
      }
    });

    return secondary;
  }

  /**
   * Load a group of fonts
   */
  async loadFontGroup(fonts, priority = 'normal') {
    const loadPromises = fonts.map(font => this.loadFont(font, priority));

    try {
      await Promise.all(loadPromises);
      console.log(`✅ Loaded ${fonts.length} ${priority} fonts`);
    } catch (error) {
      console.warn(`⚠️ Some ${priority} fonts failed to load:`, error);
    }
  }

  /**
   * Load a single font
   */
  async loadFont(font, priority = 'normal') {
    const { family, weight, style, descriptors } = font;

    // Check if Font Loading API is available
    if (!('fonts' in document)) {
      console.warn('⚠️ Font Loading API not supported');
      return;
    }

    try {
      // Create FontFace object
      const fontFace = new FontFace(family, `local('${family}')`, descriptors);

      // Load font
      const loadedFace = await fontFace.load();

      // Add to document fonts
      document.fonts.add(loadedFace);

      const key = `${family}-${weight}-${style}`;
      this.loadedFonts.add(key);

      console.log(`✅ Loaded font: ${family} (${weight}, ${style})`);

    } catch (error) {
      console.warn(`⚠️ Failed to load font: ${family}`, error);
      throw error;
    }
  }

  /**
   * Timeout helper
   */
  timeout(ms) {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Font loading timeout')), ms);
    });
  }

  /**
   * Manually load a font
   */
  async loadCustomFont(family, url, descriptors = {}) {
    if (!('fonts' in document)) {
      console.warn('⚠️ Font Loading API not supported');
      return;
    }

    try {
      const fontFace = new FontFace(family, `url(${url})`, descriptors);
      const loadedFace = await fontFace.load();
      document.fonts.add(loadedFace);

      console.log(`✅ Custom font loaded: ${family}`);
      return loadedFace;

    } catch (error) {
      console.error(`❌ Failed to load custom font: ${family}`, error);
      throw error;
    }
  }

  /**
   * Preload a font (before it's needed)
   */
  preloadFont(url, family, descriptors = {}) {
    // Add preload link
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);

    // Also load via Font Loading API
    if ('fonts' in document) {
      this.loadCustomFont(family, url, descriptors);
    }

    console.log(`⚡ Preloading font: ${family}`);
  }

  /**
   * Get loading status
   */
  getStatus() {
    return {
      network: this.networkSpeed,
      saveData: this.saveData,
      detected: this.fonts.size,
      loaded: this.loadedFonts.size,
      cached: this.areFontsCached(),
      state: document.documentElement.className.match(/fonts-\w+/)?.[0] || 'none'
    };
  }

  /**
   * Force reload fonts (clear cache)
   */
  forceReload() {
    try {
      sessionStorage.removeItem('fonts-loaded');
    } catch (e) {
      // Ignore
    }

    document.documentElement.classList.remove('fonts-loaded', 'fonts-loading', 'fonts-fallback');
    this.loadedFonts.clear();
    this.loadFonts();

    console.log('🔄 Forcing font reload');
  }
}

// Add CSS for font loading states
const fontLoadingCSS = `
  /* Font loading states */
  html.fonts-loading {
    /* Show fallback fonts while loading */
  }

  html.fonts-loaded {
    /* Custom fonts loaded */
  }

  html.fonts-fallback {
    /* Use system fonts only (Save-Data or timeout) */
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
                 Oxygen, Ubuntu, Cantarell, sans-serif !important;
  }

  /* Optional: Prevent FOIT (Flash of Invisible Text) */
  @font-face {
    font-display: swap; /* or 'fallback' or 'optional' */
  }
`;

// Inject CSS
const style = document.createElement('style');
style.textContent = fontLoadingCSS;
document.head.appendChild(style);

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.fontLoader = new AdvancedFontLoader();
    console.log('🔤 Advanced Font Loader initialized:', window.fontLoader.getStatus());
  });
} else {
  window.fontLoader = new AdvancedFontLoader();
  console.log('🔤 Advanced Font Loader initialized:', window.fontLoader.getStatus());
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AdvancedFontLoader;
}
